import React, { useEffect, useMemo, useState } from "react";
import { FormikProvider, useFormik } from "formik";
import { BannerProps, Select } from "kubra-ux-forge";
import { useTranslation } from "react-i18next";
import {
  FREEFORM_NAMESPACE,
  COMMON_NAMESPACE,
  VALIDATE_NAMESPACE,
} from "constants/i18n-namespaces";
import { Navigate, useNavigate, useLocation } from "react-router";
import { getDomain, getLabelWithKrn } from "utilities/helper";
import { getAmountInstructionText } from "utilities/amount-instruction";
import { useLabelsWithKrn } from "hooks";
import {
  postPayableCreate,
  postPayableInitiateIntent,
} from "api/IamApi/entities/ezpay";
import routing from "routing";
import { useDispatch, useSelector } from "react-redux";
import IamApi from "api/IamApi";
import { PayableType } from "types/payable";
import { setPayableSession } from "actions/payableSessionActions";
import { getPayableSession } from "selectors/payableSession";
import { IContactInfo } from "types/biller";
import { FreeformAmountField } from "types/payable";
import { getDeviceFingerprint } from "selectors/appState";
import { generateValidationSchema } from "components/extension-fields/schema";
import { Field } from "components/extension-fields/Field";
import { PaymentAmountField } from "components/extension-fields/PaymentAmountField/PaymentAmountField";
import ContinueButton from "../continue-button/continue-button";

export interface AmountFormProps {
  setBannerState: (state: BannerProps) => void;
  biller: any;
  selectedPayableType: PayableType;
  selectedBusinessUnitError: boolean;
  setSelectedBusinessUnitError: (isError: boolean) => void;
}

export const AmountForm: React.FC<AmountFormProps> = (props) => {
  const { setBannerState, biller } = props;
  const fingerprint = useSelector(getDeviceFingerprint);
  const [loading, setLoading] = useState(false);
  const payableSession = useSelector(getPayableSession);

  const selectedPayableType = props.selectedPayableType;

  const navigate = useNavigate();
  const { t, i18n } = useTranslation([FREEFORM_NAMESPACE]);

  const { getLabelsWithKrn } = useLabelsWithKrn();
  const labelSourceWithKrn = getLabelsWithKrn();
  const domain = getDomain();
  const dispatch = useDispatch();
  const location = useLocation();
  const currentChannel = location.state || { name: "Web" };

  const {
    name: payableTypeName,
    supportedBusinessUnitCodes,
    minPayableAmount,
    maxPayableAmount,
    typeKrn,
  } = selectedPayableType;

  const fieldsForFreeform: FreeformAmountField[] = [
    {
      type: "FreeformAmount",
      name: "payableAmountLabel",
      min: minPayableAmount ?? undefined,
      max: maxPayableAmount ?? undefined,
    },
  ];

  const contactUs = biller?.properties?.find((element: any) => {
    return element.name === "InstanceConfiguration";
  }) as IContactInfo;

  const businessUnitCodes = useMemo(() => {
    if (
      !supportedBusinessUnitCodes ||
      supportedBusinessUnitCodes.length === 0
    ) {
      return [];
    }

    return supportedBusinessUnitCodes.map((code) => {
      if (typeof code === "string") {
        return {
          name: getLabelWithKrn(
            labelSourceWithKrn.labelsWithKrn,
            typeKrn,
            code,
          ),
          value: code,
        };
      }
    });
  }, [supportedBusinessUnitCodes, labelSourceWithKrn, typeKrn]);

  const submitFreeformPayable = async (
    typeKrn: string,
    payableFields: unknown,
  ) => {
    setLoading(true);
    try {
      const lookupResponse = await postPayableCreate(typeKrn, payableFields);
      const payable = lookupResponse.data as PayableType;

      let nextPage: string | undefined = undefined;
      if (selectedPayableType?.extensionFields?.length > 0) {
        nextPage = routing.payableDetails.index();
      } else {
        // If no extension fields, then initiate the payment intent.
        await postPayableInitiateIntent(
          payable.krn,
          fingerprint,
          navigator.userAgent,
        );
        nextPage = routing.payableDashboard.index();
      }

      const updatedSession = await IamApi.ezpay.getPayableFromSession(domain);
      dispatch(setPayableSession(updatedSession));

      navigate(nextPage);
    } catch (error: any) {
      if (
        error.errorMessageCode === 404 &&
        selectedPayableType?.category?.toLowerCase() === "freeform"
      ) {
        const messageLabel = t("invalid-entry", { ns: VALIDATE_NAMESPACE });
        const messageBody = t("validation-error", {
          billedName: biller?.billerName,
          contactNo: contactUs.properties.phone,
        });

        setBannerState({
          active: true,
          variant: "error",
          messageLabel,
          messageBody,
          withClose: true,
          withIcon: true,
          withHyperlink: false,
        });
      }
    } finally {
      setLoading(false);
    }
  };

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: (fieldsForFreeform ?? []).reduce<Record<string, any>>(
      (values, field) => {
        values[field.name] =
          payableSession?.payable?.payableAmount ?? minPayableAmount ?? "";
        return values;
      },
      { selectedBusinessUnit: payableSession?.payable?.businessUnitCode ?? "" },
    ),
    validationSchema: generateValidationSchema(
      fieldsForFreeform,
      t,
      labelSourceWithKrn.labelsWithKrn,
      typeKrn,
    ),
    onSubmit: (values) => {
      const { payableAmountLabel } = values;
      const payableFields = {
        spaceKrn: biller.spaceKrn,
        payableAmount: Number(payableAmountLabel),
        businessUnitCode: formik.values.selectedBusinessUnit,
        domain: domain,
        channel: currentChannel.name,
        language: i18n.language,
      };

      submitFreeformPayable(typeKrn, payableFields);
    },
  });

  useEffect(() => {
    formik.validateForm();
    formik.setTouched({});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [i18n.language]);

  useEffect(() => {
    if (supportedBusinessUnitCodes?.length === 1) {
      formik.setFieldValue(
        "selectedBusinessUnit",
        supportedBusinessUnitCodes[0],
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [supportedBusinessUnitCodes]);

  if (!supportedBusinessUnitCodes || supportedBusinessUnitCodes.length === 0) {
    console.error("No supportedBusinessUnitCodes available");
    return <Navigate to={routing.index()} />;
  }

  const handleContinueClick = () => {
    if (
      supportedBusinessUnitCodes.length > 1 &&
      !formik.values.selectedBusinessUnit
    ) {
      props.setSelectedBusinessUnitError(true);
      return;
    }
    props.setSelectedBusinessUnitError(false);
    formik.handleSubmit();
  };

  const payableAmountLabel = getLabelWithKrn(
    labelSourceWithKrn.labelsWithKrn,
    typeKrn,
    "payableAmountLabel",
  );
  const payableName = getLabelWithKrn(
    labelSourceWithKrn.labelsWithKrn,
    typeKrn,
    selectedPayableType.name,
  );
  const displayLabel =
    payableAmountLabel === "payableAmountLabel"
      ? t("default-amount-label", { payableTypeName: payableName })
      : payableAmountLabel;
  const amountInstructionText = getAmountInstructionText(
    minPayableAmount,
    maxPayableAmount,
    t,
  );

  return (
    <div>
      <FormikProvider value={formik}>
        <form
          onSubmit={formik.handleSubmit}
          key={i18n.language}
          data-testid="payable-amount-form"
          className="form-container"
        >
          {supportedBusinessUnitCodes.length > 1 && (
            <div className="ez-pay-payable-row">
              <label
                htmlFor={`select${payableTypeName}`}
                className="validate-label"
              >
                {getLabelWithKrn(
                  labelSourceWithKrn.labelsWithKrn,
                  typeKrn,
                  "businessUnitTypeSelection",
                )}
              </label>
              <Select
                name="selectedBusinessUnit"
                label={""}
                onChange={(option: { id: string; image: string }[]) => {
                  formik.setFieldValue("selectedBusinessUnit", option[0].id);
                  props.setSelectedBusinessUnitError(false);
                }}
                defaultValue={formik.values.selectedBusinessUnit}
                placeholder={t("select", { ns: COMMON_NAMESPACE })}
                options={businessUnitCodes.map((code) => ({
                  id: code?.value,
                  name: getLabelWithKrn(
                    labelSourceWithKrn.labelsWithKrn,
                    typeKrn,
                    code?.name,
                  ),
                }))}
                hasError={
                  formik.touched.selectedBusinessUnit &&
                  formik.values.selectedBusinessUnit === ""
                }
                canDeselect={false}
              />
            </div>
          )}

          <PaymentAmountField
            name="payableAmountLabel"
            label={displayLabel}
            instructionText={amountInstructionText}
            placeholder={getLabelWithKrn(
              labelSourceWithKrn.labelsWithKrn,
              typeKrn,
              minPayableAmount?.toString() ?? "",
            )}
            minPayableAmount={
              formik.values.payableAmountLabel ?? minPayableAmount ?? undefined
            }
            maxPayableAmount={maxPayableAmount ?? undefined}
          />
          <ContinueButton
            loading={loading}
            onClick={() => handleContinueClick()}
          />
        </form>
      </FormikProvider>
    </div>
  );
};

export default AmountForm;
